<!--
  @component Navbar.MenuItem

  A menu item component for the Navbar, supporting nested submenus and keyboard navigation.

  @prop href - The URL the menu item links to (required)
  @prop label - The text displayed for the menu item (required)
  @prop bold - Whether the text should be bold (default: false)
  @prop isActive - Whether this menu item represents the current page (default: false)
  @prop children - A Svelte snippet for nested submenu content
  @prop css - Custom CSS styles to apply to the menu item

  Example:
  <Navbar.MenuItem href="/products" label="Products">
    <Navbar.MenuSubmenu label="Products">
      <Navbar.MenuItem href="/products/electronics" label="Electronics" />
      <Navbar.MenuItem href="/products/clothing" label="Clothing" />
    </Navbar.MenuSubmenu>
  </Navbar.MenuItem>
-->

<script lang="ts">
  import { getContext, setContext } from 'svelte';
  import { css } from '@agapi/ui/panda/css';
  import { menuItemStyles } from '../navbar.styles';
  import type { NavbarStore } from '../navbar-store.svelte';
  import { CaretRight } from 'phosphor-svelte';
  import { goto } from '$app/navigation';
  import type { NavbarMenuItemProps, NavbarMenuItemStore } from '../navbar.types';

  const { href, label, children, isActive = false, bold = false, css: cssProp }: NavbarMenuItemProps = $props();


  const classes = menuItemStyles.raw();
  const navbarStore = getContext<NavbarStore>('navbar');
  const id = $props.id();

  /**
   * Toggles the open state of the menu item.
   * @param id - The unique identifier of the menu item
   */
  const setMenuItem = (id: string) => {
    if (navbarStore.openMenuItemId?.includes(id)) {
      const filtered = navbarStore.openMenuItemId?.filter(itemId => itemId !== id);
      navbarStore.setOpenMenuItem(filtered);
    } else {
      navbarStore.setOpenMenuItem([...(navbarStore.openMenuItemId || []), id]);
    }
  }

  /**
   * Handles click events on the menu item.
   * @param e - The mouse event
   */
  const onclick = (e: MouseEvent) => {
    if (!children) {
        goto(href as unknown as URL);
        navbarStore.closeMenu();
      return;
    }

    e.preventDefault();
    setMenuItem(id);
  }

  /**
   * Handles keydown events for keyboard navigation.
   * @param e - The keyboard event
   */
  const onkeydown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
        if (children) {
          e.preventDefault();
          setMenuItem(id);
        }
        break;
      case 'Escape':
        navbarStore.closeMenu();
        break;
    }
  }

  const isSubmenuOpen = $derived(navbarStore.openMenuItemId?.includes(id));
  setContext<NavbarMenuItemStore>('navbar-menu-item', { id, depth: navbarStore.menuDepth + 1 });
</script>

<div class={css(classes.root, cssProp)} aria-expanded={isSubmenuOpen}>
  <a
    {onclick}
    {onkeydown}
    href={href}
    role="menuitem"
    class={css(classes.label, {
      fontWeight: bold ? 'bold' : undefined,
    })}
    aria-current={isActive ? 'page' : undefined}
    aria-expanded={children ? isSubmenuOpen : undefined}
    aria-haspopup={children ? 'true' : 'false'}
  >
    {label}
    <CaretRight class="caret" />
  </a>
  {#if children}
    {@render children()}
  {/if}
</div>
